<template>
    <transition name="bmv-modal-fade">
        <div
            v-if="current"
            class="bmv-modal-overlay"
            role="alertdialog"
            aria-modal="true"
            data-testid="bmv-dialog"
            @mousedown.self="dismiss"
        >
            <div class="bmv-dialog" @mousedown.stop>
                <div class="bmv-dialog-icon" :class="`bmv-dialog-icon--${current.variant || 'primary'}`">
                    <i :class="iconClass"></i>
                </div>
                <h2 v-if="current.title" class="bmv-dialog-title">{{ current.title }}</h2>
                <p v-if="current.message" class="bmv-dialog-message" v-html="current.message"></p>

                <div class="bmv-dialog-actions">
                    <BmvButton
                        v-if="current.kind === 'confirm'"
                        variant="ghost"
                        size="md"
                        @click="cancel"
                    >
                        {{ current.cancelLabel || defaultCancel }}
                    </BmvButton>
                    <BmvButton
                        :variant="current.variant === 'danger' ? 'danger' : 'primary'"
                        size="md"
                        @click="confirm"
                    >
                        {{ current.confirmLabel || defaultConfirm }}
                    </BmvButton>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import BmvButton from './BmvButton.vue'

export default {
    name: 'BmvDialog',
    components: { BmvButton },
    data() {
        return {
            queue: []
        }
    },
    computed: {
        current() {
            return this.queue[0] || null
        },
        iconClass() {
            if (!this.current) return ''
            switch (this.current.variant) {
                case 'danger': return 'fal fa-exclamation-triangle'
                case 'warning': return 'fal fa-exclamation-circle'
                case 'success': return 'fal fa-check-circle'
                case 'info': return 'fal fa-info-circle'
                default: return this.current.kind === 'alert' ? 'fal fa-info-circle' : 'fal fa-question-circle'
            }
        },
        defaultConfirm() {
            return (this.$t && this.$t('common.ok')) || 'OK'
        },
        defaultCancel() {
            return (this.$t && this.$t('common.cancel')) || 'Cancel'
        }
    },
    mounted() {
        document.addEventListener('keydown', this.onKey)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onKey)
    },
    methods: {
        open(options) {
            return new Promise((resolve) => {
                this.queue.push({
                    kind: 'confirm',
                    ...options,
                    _resolve: resolve
                })
            })
        },
        confirm() {
            const item = this.queue.shift()
            if (item && typeof item.onConfirm === 'function') item.onConfirm()
            if (item && item._resolve) item._resolve(true)
        },
        cancel() {
            const item = this.queue.shift()
            if (item && typeof item.onCancel === 'function') item.onCancel()
            if (item && item._resolve) item._resolve(false)
        },
        dismiss() {
            if (!this.current) return
            if (this.current.kind === 'alert') this.confirm()
            else this.cancel()
        },
        onKey(e) {
            if (!this.current) return
            if (e.key === 'Escape') this.dismiss()
            if (e.key === 'Enter') this.confirm()
        }
    }
}
</script>

<style scoped>
.bmv-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-4);
    z-index: 10000;
    backdrop-filter: blur(2px);
}

.bmv-dialog {
    background: var(--v2-surface-0);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-lg);
    padding: var(--v2-space-6);
    width: 100%;
    max-width: 420px;
    text-align: center;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-1);
}

.bmv-dialog-icon {
    width: 48px;
    height: 48px;
    border-radius: var(--v2-radius-full);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--v2-space-4);
    font-size: 1.25rem;
}

.bmv-dialog-icon--primary { background: var(--v2-primary-soft); color: var(--v2-primary); }
.bmv-dialog-icon--danger { background: var(--v2-danger-soft); color: var(--v2-danger); }
.bmv-dialog-icon--warning { background: var(--v2-warning-soft); color: var(--v2-warning); }
.bmv-dialog-icon--success { background: var(--v2-success-soft); color: var(--v2-success); }
.bmv-dialog-icon--info { background: var(--v2-info-soft); color: var(--v2-info); }

.bmv-dialog-title {
    margin: 0 0 var(--v2-space-2);
    font-size: var(--v2-text-lg-size);
    font-weight: var(--v2-font-weight-semibold);
}

.bmv-dialog-message {
    margin: 0 0 var(--v2-space-5);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-2);
    line-height: var(--v2-text-sm-line);
}

.bmv-dialog-actions {
    display: flex;
    justify-content: center;
    gap: var(--v2-space-2);
}

.bmv-modal-fade-enter-active,
.bmv-modal-fade-leave-active {
    transition: opacity var(--v2-transition-base);
}

.bmv-modal-fade-enter,
.bmv-modal-fade-leave-to {
    opacity: 0;
}
</style>
